/**
 * lessonReducer
 *
 * Pure function. Takes current state + an action, returns new state.
 * No React. No side effects. Fully unit-testable.
 *
 * State shape:
 * {
 *   progressById:       { [stepItemId]: { completed: bool } }
 *   activeStepItemId:   string  — the stepItem being actively worked on
 *   viewingStepId:      string | null — step shown in LessonPanel, null = follow active
 *   celebrationQueue:   Array<"step" | "lesson" | "course">
 *   saveRevision:       number  — increments on every progress change, triggers a save
 *   lastSavedRevision:  number  — last revision confirmed saved to DB
 * }
 *
 * Actions:
 *   CONTINUE            — user taps continue on current stepItem
 *   BACK                — user taps back
 *   CELEBRATION_DONE    — celebration animation finished
 *   BROWSE_STEP         — user taps a step in LessonPanel ({ stepId: string | null })
 *   SAVE_OK             — DB save succeeded ({ revision: number })
 *   SAVE_FAIL           — DB save failed ({ revision: number })
 *   DEV_OVERRIDE_PROGRESS — CHEATS panel: force-set progressById ({ progressById })
 */

import {
  getNextStepItemId,
  getPrevStepItemId,
  enqueueCelebrations,
  isNodeCompleted,
} from './buildRecipeIndex';


function lessonReducer(idx, state, action) {
  switch (action.type) {

    // -----------------------------------------------------------------------
    case 'CONTINUE': {
      // Blocked during active celebration
      if (state.celebrationQueue.length > 0) return state;

      const id = state.activeStepItemId;
      const alreadyDone = !!state.progressById[id]?.completed;
      const skippable = !!idx.isSkippable[id];

      if (!alreadyDone && !skippable) {
        // Mark this stepItem as completed
        const progressById = {
          ...state.progressById,
          [id]: { completed: true },
        };

        // Compute which celebration levels just completed
        const celebrations = enqueueCelebrations(idx, id, progressById);

        if (celebrations.length > 0) {
          // Step/lesson/course boundary reached — celebrate first, CELEBRATION_DONE advances
          return {
            ...state,
            progressById,
            celebrationQueue: celebrations,
            saveRevision: state.saveRevision + 1,
          };
        }

        // No boundary crossed — advance immediately within the same step
        const nextId = getNextStepItemId(idx, id);
        if (!nextId) return { ...state, progressById, saveRevision: state.saveRevision + 1 };
        return {
          ...state,
          progressById,
          activeStepItemId: nextId,
          saveRevision: state.saveRevision + 1,
        };
      }

      if (!alreadyDone && skippable) {
        const progressById = {
          ...state.progressById,
          [id]: { completed: true },
        };

        // Check if skipping this item finished a step/lesson/course.
        // If so, celebrate first — same as mandatory items. CELEBRATION_DONE advances.
        const celebrations = enqueueCelebrations(idx, id, progressById);
        if (celebrations.length > 0) {
          return {
            ...state,
            progressById,
            celebrationQueue: celebrations,
            saveRevision: state.saveRevision + 1,
          };
        }

        // No boundary crossed — advance within the same step.
        const nextId = getNextStepItemId(idx, id);
        if (!nextId) return { ...state, progressById, saveRevision: state.saveRevision + 1 };
        return {
          ...state,
          progressById,
          activeStepItemId: nextId,
          saveRevision: state.saveRevision + 1,
        };
      }

      // Already done — advance within the same step only (no save).
      // At a step boundary, stop: this means the user is replaying a completed step
      // and has reached its end. Lesson.jsx will close the stepper.
      const nextId = getNextStepItemId(idx, id);
      if (!nextId) return state;

      const crossingStepBoundary = idx.parentOf[nextId] !== idx.parentOf[id];
      if (crossingStepBoundary) return state;

      return {
        ...state,
        activeStepItemId: nextId,
        viewingStepId: state.viewingStepId,
      };
    }

    // -----------------------------------------------------------------------
    case 'BACK': {
      const prevId = getPrevStepItemId(idx, state.activeStepItemId);
      if (!prevId) return state; // already at the start

      // Don't cross step boundaries on back — handled by LessonStepper closing
      const prevStepId = idx.parentOf[prevId];
      const currentStepId = idx.parentOf[state.activeStepItemId];
      if (prevStepId !== currentStepId) return state;

      return {
        ...state,
        activeStepItemId: prevId,
      };
    }

    // -----------------------------------------------------------------------
    case 'CELEBRATION_DONE': {
      if (state.celebrationQueue.length === 0) return state;

      const [current, ...rest] = state.celebrationQueue;

      if (current === 'course') {
        // Course is done — clear the queue. UI derives CourseCompleted from
        // isNodeCompleted on the course, not from celebrationQueue.
        return { ...state, celebrationQueue: rest };
      }

      if (rest.length > 0) {
        // More celebrations queued — show next one without advancing cursor
        return { ...state, celebrationQueue: rest };
      }

      // All celebrations done — advance cursor to next stepItem
      const nextId = getNextStepItemId(idx, state.activeStepItemId);
      if (!nextId) {
        // No next item (end of course) — clear queue and stay
        return { ...state, celebrationQueue: [] };
      }

      return {
        ...state,
        celebrationQueue: [],
        activeStepItemId: nextId,
        viewingStepId: null, // reset to follow active
      };
    }

    // -----------------------------------------------------------------------
    case 'BROWSE_STEP': {
      // action.stepId: string to view a specific step, or null to reset to follow active
      return {
        ...state,
        viewingStepId: action.stepId ?? null,
      };
    }

    // -----------------------------------------------------------------------
    case 'NAVIGATE_TO_STEP_ITEM': {
      // Move cursor directly to a specific step item (e.g. first non-hideOnReplay item on replay).
      if (!action.stepItemId) return state;
      return {
        ...state,
        activeStepItemId: action.stepItemId,
        viewingStepId: null,
      };
    }

    // -----------------------------------------------------------------------
    case 'NAVIGATE_TO_STEP': {
      // User taps "Replay" on a completed step in LessonPanel — move cursor to
      // the first item of that step so LessonStepper shows the right content.
      // Does NOT increment saveRevision (replay is ephemeral; progress is unchanged).
      const firstItemId = idx.childrenOf[action.stepId]?.[0];
      if (!firstItemId) return state;
      return {
        ...state,
        activeStepItemId: firstItemId,
        viewingStepId: null, // follow active
      };
    }

    // -----------------------------------------------------------------------
    case 'SAVE_OK': {
      // Only update if this is the same or newer revision we're tracking
      if (action.revision < state.lastSavedRevision) return state;
      return {
        ...state,
        lastSavedRevision: action.revision,
      };
    }

    // -----------------------------------------------------------------------
    case 'SAVE_FAIL': {
      // Log but don't crash — the save effect will retry on next revision change
      console.error('[lessonReducer] Save failed for revision', action.revision);
      return state;
    }

    // -----------------------------------------------------------------------
    case 'MARK_ITEM_COMPLETE': {
      // Silently marks a specific stepItem as completed by ID.
      // Does NOT touch activeStepItemId, does NOT trigger celebrations.
      // Used for the onboarding intro: mark the intro video complete without
      // disrupting the global cursor or showing a celebration screen.
      const id = action.stepItemId;
      if (!id || state.progressById[id]?.completed) return state;
      return {
        ...state,
        progressById: {
          ...state.progressById,
          [id]: { completed: true },
        },
        saveRevision: state.saveRevision + 1,
      };
    }

    // -----------------------------------------------------------------------
    case 'DEV_OVERRIDE_PROGRESS': {
      // CHEATS panel: force-set progress and recompute cursor
      const progressById = action.progressById || {};
      return {
        ...state,
        progressById,
        activeStepItemId: action.activeStepItemId ?? state.activeStepItemId,
        saveRevision: state.saveRevision + 1,
        celebrationQueue: [],
      };
    }

    // -----------------------------------------------------------------------
    default:
      console.warn('[lessonReducer] Unknown action type:', action.type);
      return state;
  }
}


// ---------------------------------------------------------------------------
// Initial state factory
// ---------------------------------------------------------------------------

/**
 * Creates the initial reducer state from migrated progress data.
 *
 * @param {object} migratedProgress - Result of migrateProgress()
 *   { progressById, activeStepItemId }
 */
function createInitialState(migratedProgress) {
  const { progressById, activeStepItemId } = migratedProgress;

  return {
    progressById: progressById || {},
    activeStepItemId: activeStepItemId || null,
    viewingStepId: null,
    celebrationQueue: [],
    saveRevision: 0,
    lastSavedRevision: 0,
  };
}


// ---------------------------------------------------------------------------
// Selectors (pure, derived from state + idx)
// ---------------------------------------------------------------------------

/**
 * Returns the stepId currently being viewed in LessonPanel.
 * Follows active if viewingStepId is null.
 */
function selectViewingStepId(idx, state) {
  if (state.viewingStepId) return state.viewingStepId;
  return idx.parentOf[state.activeStepItemId] ?? null;
}

/**
 * Returns the first celebration in the queue, or null.
 */
function selectActiveCelebration(state) {
  return state.celebrationQueue[0] ?? null;
}

/**
 * Returns true if the entire course is completed.
 */
function selectIsCourseCompleted(idx, state) {
  const courseId = idx.courseIds[0];
  if (!courseId) return false;
  return isNodeCompleted(idx, courseId, state.progressById);
}

/**
 * Returns true if the current lesson is completed.
 */
function selectIsCurrentLessonCompleted(idx, state) {
  const stepId = idx.parentOf[state.activeStepItemId];
  if (!stepId) return false;
  const lessonId = idx.parentOf[stepId];
  if (!lessonId) return false;
  return isNodeCompleted(idx, lessonId, state.progressById);
}

/**
 * Returns the lesson recipe node for the current active stepItem.
 */
function selectCurrentLessonNode(idx, state) {
  const stepId = idx.parentOf[state.activeStepItemId];
  if (!stepId) return null;
  const lessonId = idx.parentOf[stepId];
  if (!lessonId) return null;
  return idx.nodeOf[lessonId] ?? null;
}

/**
 * Returns true if the current stepItem has already been completed.
 */
function selectIsActiveItemCompleted(state) {
  return !!state.progressById[state.activeStepItemId]?.completed;
}

/**
 * Returns a save payload suitable for persisting to DB.
 */
function selectSavePayload(state) {
  return {
    progressById: state.progressById,
    activeStepItemId: state.activeStepItemId,
  };
}


export {
  lessonReducer,
  createInitialState,
  selectViewingStepId,
  selectActiveCelebration,
  selectIsCourseCompleted,
  selectIsCurrentLessonCompleted,
  selectCurrentLessonNode,
  selectIsActiveItemCompleted,
  selectSavePayload,
};
